import React, { useEffect, useState } from "react";
import { Alert, Button } from "react-bootstrap";
import { useAuthUser } from "../../features/auth/authHooks";
import { apiService } from "../../common/api/apiService";
import { API_ENDPOINTS } from "../../common/api/apiEndpoints";

interface PersonIdentityData {
  hasPersonalIdentityCode: boolean;
  firstName?: string;
  lastName?: string;
  issuedAt?: string;
  personalIdentityCode?: string;
  updatedAt?: string;
}

const StrongAuthBanner: React.FC = () => {
  const { ownerAccountId, isLoggedIn, isOwnerUser } = useAuthUser();
  const [loading, setLoading] = useState(true);
  const [hasStrongAuth, setHasStrongAuth] = useState<boolean | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const checkStrongAuth = async () => {
      if (!isLoggedIn || !ownerAccountId || !isOwnerUser) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const data: PersonIdentityData = await apiService.get(
          `${API_ENDPOINTS.IDENTITY.GET_PERSON_IDENTITY_DATA}?accountId=${ownerAccountId}`,
        );
        setHasStrongAuth(data.hasPersonalIdentityCode === true);
      } catch (e: any) {
        console.error("Error checking strong authentication status:", e);
        // If we get an error, we don't show the banner
        setHasStrongAuth(true);
        setError(
          "Vahvan tunnistautumisen tilan tarkistus epäonnistui. Kyseessä voi olla väliaikainen häiriö.",
        );
      } finally {
        setLoading(false);
      }
    };

    checkStrongAuth();
  }, [isLoggedIn, ownerAccountId]);

  const handleStrongAuth = () => {
    // Redirect to BFF endpoint which will redirect to identity server's strong authentication challenge
    // The returnUrl will bring the user back to the IdentityVerified page after successful authentication
    const returnUrl = encodeURIComponent(
      "/Account/IdentityVerified?omaliittyma=true",
    );
    window.location.href = `/local-api/StrongAuth/challenge?returnUrl=${returnUrl}`;
  };

  // Don't show anything while loading or if user has already done strong auth
  if (loading) {
    return null;
  }

  if (error) {
    return (
      <Alert variant="warning" className="mb-24">
        <p className="text-danger mt-8 mb-0">{error}</p>
      </Alert>
    );
  }

  if (hasStrongAuth === true) {
    return null;
  }

  return (
    <Alert variant="warning" className="mb-24">
      <div className="d-flex flex-column flex-md-row align-items-start align-items-md-center gap-16">
        <div className="flex-grow-1">
          <Alert.Heading className="h5 mb-8">
            <span className="bi bi-shield-exclamation me-8"></span>
            Palvelun käyttö edellyttää vahvaa tunnistautumista
          </Alert.Heading>
          <p className="mb-1">
            Verohallinnon vaatima vahva tunnistautuminen tarvitaan vuoden 2025
            vuokratulojen veroraportointia varten. Tunnistautuminen tehdään
            turvallisesti Telian tunnistuspalvelussa pankkitunnuksilla tai
            mobiilivarmenteella. Suosittelemme tekemään sen heti, jotta
            OMAliittymän käyttö jatkuu sujuvasti.
          </p>
          <p className="mb-1">
            <ul>
              <li>
                15.1.2026 alkaen OMAliittymä ei ole käytettävissä ilman
                tunnistautumista
              </li>
              <li>
                1.2.2026 alkaen ei verottajan ohjeen mukaisesti tilityksiä voida
                maksaa ilman tunnistautumista.
              </li>
            </ul>
          </p>
          <p className="mb-1">
            <a
              target="_blank"
              href="https://www.vero.fi/henkiloasiakkaat/verokortti-ja-veroilmoitus/tulot/ansiotulot/myynti-digitaalisilla-alustoilla"
              rel="noreferrer"
              className="text-decoration-underline"
            >
              Lue lisää Verohallinnon sivuilta &gt;&gt;
            </a>
          </p>
          {error && <p className="text-danger mt-8 mb-0">{error}</p>}
        </div>
        <Button
          variant="primary"
          onClick={handleStrongAuth}
          className="text-nowrap"
        >
          <span className="bi bi-shield-check me-8"></span>
          Siirry tunnistautumaan
        </Button>
      </div>
    </Alert>
  );
};

export default StrongAuthBanner;
